'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Key } from 'swr/_internal';
import useSWRMutation, {
  type MutationFetcher,
  type SWRMutationConfiguration,
} from 'swr/mutation';

import type { ApiResponse } from '@/api/base';
import { timestampByUTC } from '@/helpers/dateTime';
import { useLocalStorageStore } from '@/store';

// extended hooks to handle response properly with possibility to cancel request

type SaveErrorsCountToStorage = {
  storageName: string;
  timeLeftToClear: number;
};

export interface ApiConfig {
  saveErrorsCountToStorage?: SaveErrorsCountToStorage;
}
type ErrorCountStorageType = { createdAt: number; requestCount: number };

interface SwrMutationParams<Data = any, Error = any> {
  key: Key; // used to save in cache
  fetcher: (...args: any[]) => Promise<ApiResponse<Data>>;
  options?: SWRMutationConfiguration<Data, Error, Key> & ApiConfig;
}

export const useSwrMutation = <Data = any, Error = any>({
  key,
  fetcher,
  options,
}: SwrMutationParams<Data>) => {
  const { saveErrorsCountToStorage, onError, ...swrConfig } = options || {};
  const {
    signal,
    requestCount,
    resetRequestCount,
    tryCancelRequest,
    saveError,
  } = useHandleRequestCancel(saveErrorsCountToStorage);

  const fetcherExtended: MutationFetcher<Data> = async (
    _key: string,
    { arg }: { arg: any }
  ) => {
    const response = await fetcher(arg, {
      signal,
    });

    if (response?.error) {
      throw response.error;
    }

    return response?.data as Data;
  };

  const handleError = (
    err: Error,
    key: string,
    config: Readonly<SWRMutationConfiguration<Data, Error>>
  ) => {
    saveError();

    onError && onError(err, key, config);
  };

  const swr = useSWRMutation(key, fetcherExtended, {
    ...swrConfig,
    onError: handleError,
  });

  return {
    ...swr,
    requestCount,
    cancel: tryCancelRequest,
    resetRequestCount,
  };
};

export const useHandleRequestCancel = (
  saveErrorsCountToStorage: SaveErrorsCountToStorage | undefined
) => {
  const {
    get: getLocalStorage,
    replace: replaceLocalStorage,
    remove: removeLocalStorage,
  } = useLocalStorageStore.getState();
  const [requestCount, setRequestCount] = useState(0);

  const controller = useMemo(() => new AbortController(), []);
  const signal = controller.signal;

  const tryCancelRequest = useCallback(() => controller.abort(), [controller]);

  useEffect(() => {
    if (saveErrorsCountToStorage) {
      const storageName = `errors-count-new-${saveErrorsCountToStorage.storageName}`;
      const values = getLocalStorage<ErrorCountStorageType | null>(
        storageName,
        null
      );

      if (
        values != null &&
        timestampByUTC() - values.createdAt <=
          saveErrorsCountToStorage.timeLeftToClear
      ) {
        setRequestCount(values.requestCount);
      }
    }
  }, [saveErrorsCountToStorage, removeLocalStorage, getLocalStorage]);

  const resetRequestCount = useCallback(() => {
    tryCancelRequest();

    if (saveErrorsCountToStorage) {
      const { storageName } = saveErrorsCountToStorage;

      removeLocalStorage(`errors-count-new-${storageName}`);
    }

    setRequestCount(0);
  }, [saveErrorsCountToStorage, tryCancelRequest, removeLocalStorage]);

  const saveError = () => {
    if (!saveErrorsCountToStorage) return;

    const { storageName } = saveErrorsCountToStorage;
    const errorsCountLocalStorageName = `errors-count-new-${storageName}`;
    const valueErrorsCountLocalStorage =
      getLocalStorage<ErrorCountStorageType | null>(
        errorsCountLocalStorageName,
        null
      );

    setRequestCount((s) => ++s);

    if (valueErrorsCountLocalStorage == null) {
      const timestamp = timestampByUTC();

      replaceLocalStorage(errorsCountLocalStorageName, {
        createdAt: timestamp,
        requestCount: 1,
      });
      return;
    }

    replaceLocalStorage(errorsCountLocalStorageName, {
      createdAt: valueErrorsCountLocalStorage.createdAt,
      requestCount: valueErrorsCountLocalStorage.requestCount + 1,
    });
  };

  return {
    signal,
    requestCount,
    resetRequestCount,
    tryCancelRequest,
    saveError,
  };
};
